import type { components } from "@majitap/sdk";
import { allPages } from "@majitap/ui";
import { queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";
import type { ApprovalStatus } from "./labels";

type Schemas = components["schemas"];
export type Partner = Schemas["Partner"];
export type PartnerMember = Partner["members"][number];
export type PartnerRole = PartnerMember["role"];
export type Payout = Partner["payout"];
type Activity = Schemas["Activity"];

export const PARTNER_ROLES: Record<PartnerRole, { label: string; help: string }> = {
  owner: { label: "Owner", help: "Runs the account: contact and payout details, members, the fleet" },
  manager: { label: "Manager", help: "Manages the fleet; looks at the rest" },
  member: { label: "Member", help: "Only looks: changes nothing" },
};

export const partnerQuery = (id: string) =>
  queryOptions({
    queryKey: ["partner", id],
    queryFn: () => ok(api.GET("/v1/admin/partners/{id}", { params: { path: { id } } })),
  });

/** What each lifecycle step is called, and from where it can be taken (`INVALID_PARTNER_TRANSITION` otherwise). */
export function partnerActionsFor(status: ApprovalStatus) {
  return {
    approve: status === "pending" || status === "suspended",
    reject: status === "pending",
    suspend: status === "approved",
  };
}

/** Where the payout details stand: none yet, waiting for a second person, or approved. */
export function payoutState(payout: Payout): "none" | "waiting" | "approved" {
  if (!payout.method || !payout.destination) return "none";
  return payout.approved ? "approved" : "waiting";
}

/**
 * The latest change to the payout details, from the audit trail: what they were, who changed
 * them (a partner owner in the portal, or staff) and when. With no change since, it's whoever
 * created the partner: the details given then count as theirs. Reading the trail needs `staff.manage`.
 */
export function lastPayoutChange(
  entries: Activity[],
): { before: Partial<Payout> | null; by: string | null; byName: string | null; byPartner: boolean; at: string } | null {
  const change = entries.find((e) => e.verb === "updated" && e.diff?.payout);
  if (change)
    return {
      before: (change.diff?.payout?.from as Partial<Payout> | undefined) ?? null,
      by: change.actor_user_id,
      byName: change.actor?.name ?? null,
      byPartner: change.payload?.by === "partner",
      at: change.created_at,
    };
  const created = entries.find((e) => e.verb === "created");
  return created
    ? {
        before: null,
        by: created.actor_user_id,
        byName: created.actor?.name ?? null,
        byPartner: false,
        at: created.created_at,
      }
    : null;
}

export interface PartnerFields {
  name: string;
  tax_pin?: string | null;
  email?: string | null;
  phone?: string | null;
  payment_term_id?: string | null;
  etims_invoice_required?: boolean;
}

export interface PayoutFields {
  payout_method: "mpesa" | "bank" | null;
  payout_destination: string | null;
  payout_account_name: string | null;
  payout_bank_code: string | null;
}

const path = (id: string) => ({ params: { path: { id } } });

export const partnerActions = {
  create: (body: PartnerFields & { owner?: { phone_number: string; name: string } }) =>
    ok(api.POST("/v1/admin/partners", { body })),
  update: (id: string, body: Partial<PartnerFields> | PayoutFields) =>
    ok(api.PATCH("/v1/admin/partners/{id}", { ...path(id), body })),
  approve: (id: string, reason?: string) =>
    ok(api.POST("/v1/admin/partners/{id}/approve", { ...path(id), body: { reason } })),
  reject: (id: string, reason?: string) =>
    ok(api.POST("/v1/admin/partners/{id}/reject", { ...path(id), body: { reason } })),
  suspend: (id: string, reason?: string) =>
    ok(api.POST("/v1/admin/partners/{id}/suspend", { ...path(id), body: { reason } })),
  approvePayout: (id: string) => ok(api.POST("/v1/admin/partners/{id}/payout-details/approve", path(id))),
  addMember: (id: string, body: { phone_number: string; name: string; role: PartnerRole }) =>
    ok(api.POST("/v1/admin/partners/{id}/members", { ...path(id), body })),
  updateMember: (id: string, userId: string, body: { role?: PartnerRole; status?: "active" | "inactive" }) =>
    ok(
      api.PATCH("/v1/admin/partners/{id}/members/{user_id}", {
        params: { path: { id, user_id: userId } },
        body,
      }),
    ),
  removeMember: (id: string, userId: string) =>
    ok(api.DELETE("/v1/admin/partners/{id}/members/{user_id}", { params: { path: { id, user_id: userId } } })),
};

/** Every partner, by name: for pickers (a partner's rates, payouts). */
export const partnerOptionsQuery = queryOptions({
  queryKey: ["partners", "options"],
  staleTime: 5 * 60_000,
  queryFn: async () =>
    (await allPages((cursor) => ok(api.GET("/v1/admin/partners", { params: { query: { cursor, limit: 100 } } })))).map(
      (p) => ({ value: p.id, label: p.name }),
    ),
});
